import apiClient from '../../../Services/ApiClient';
import { DiscourseQuery, Document } from '../../../graphql/graphql';
import { QueryItem } from '../../../Interfaces/QueryDataItem';
import { discourseDocumentItemType } from '../Enums/discourseDocumentItemType.const';
import { DiscourseValues } from './DiscourseContext';
import { DocumentContentSubType } from '../../Documents/Enums/documentContentSubType.enum';
import { DiscourseTravelItemExtra } from '../Components/DiscourseTravel/discourseTravel.helpers';
import moment from 'moment';

export type DiscourseDocument = QueryItem<DiscourseQuery['document']>;
export type DiscourseDocumentItem = NonNullable<
	DiscourseDocument['items']
>[number];

export const hasDiscourseMainDiagnosis = (diagnosis: unknown): boolean => {
	if (!diagnosis) {
		return false;
	}

	if (typeof diagnosis === 'string') {
		return diagnosis.trim().length > 0;
	}

	if (typeof diagnosis === 'object') {
		const d = diagnosis as { label?: unknown; value?: unknown };
		return Boolean(d.label && d.value);
	}

	return false;
};

export const hasDiscourseSickLeave = (
	items?: DiscourseDocumentItem[] | null,
): boolean => {
	return (
		items?.some(item => item?.type === discourseDocumentItemType.sickLeave) ??
		false
	);
};

export const getDiscourseSickLeaveDocumentId = (
	items?: DiscourseDocumentItem[] | null,
): string | undefined => {
	const sickLeaveItem = items?.find(
		item => item?.type === discourseDocumentItemType.sickLeave,
	);

	return sickLeaveItem?.extra?.id as string | undefined;
};

export const getSickLeaveTreatmentUrl = (
	patientId: string,
	sickLeaveDocumentId: string,
): string => {
	const params = new URLSearchParams({
		handle: 'treatment',
		treatmentStep: 'CSSZSickLeaveCertificate',
		sickLeaveId: sickLeaveDocumentId,
	});

	return `/profil-pacienta/${patientId}?${params.toString()}`;
};

export const saveDiscourse = async (activeDiscourseId: string) => {
	return await apiClient.patch(`/document/${activeDiscourseId}`, {
		type: 'DEKURZ',
	});
};

export const prepareSaveDiscourseItems = (values: DiscourseValues) => {
	return prepareDiscourseItems(
		values.isDatetimeManual
			? values
			: {
					...values,
					date: moment().toISOString(),
					time: moment(),
			  },
	);
};

export const discardDiscourse = async (activeDiscourseId: string) => {
	return await apiClient.patch(`/document/${activeDiscourseId}`, {
		type: 'DEKURZ_DISCARTED',
	});
};

export const stashDiscourse = async (activeDiscourseId: string, data: any) => {
	return await apiClient.patch(`/document/${activeDiscourseId}`, data);
};

export const deleteDiscourseAttachment = async (
	activeDiscourseId: string,
	attachmentId: string,
) => {
	return await apiClient.delete(
		`/document/${activeDiscourseId}/item/${attachmentId}`,
	);
};

export const initializeNewDiscourse = async (
	workplaceId: string,
	patientId: string,
	loggedUserId: string,
	data?: object,
) => {
	return await apiClient.post('/document/', {
		workplaceId,
		userId: patientId,
		createdById: loggedUserId,
		type: 'DEKURZ_DRAFT',
		children: data,
	});
};

export const attachToDiscourse = async (
	activeDiscourseId: string,
	attachmentId: string,
	type: string,
	title: string,
	sentToPatient: boolean,
	folderId?: string,
) => {
	return await apiClient.post(`/document/${activeDiscourseId}/item`, {
		type: type,
		extra: {
			id: attachmentId,
			title,
			sentToPatient: sentToPatient,
			folderId,
		},
	});
};

/**
 * Attaches prescription to dekurz draft
 * @param activeDiscourseId
 * @param prescriptionId
 * @returns {Promise}
 */
export const attachPrescription = async (
	activeDiscourseId: string,
	prescriptionId: string,
	title?: string,
) => {
	return await apiClient.post(`/document/${activeDiscourseId}/item`, {
		type: 'prescription',
		extra: {
			id: prescriptionId,
			title,
		},
	});
};

export const attachDiscourseTravel = async (
	activeDiscourseId: string,
	extra: DiscourseTravelItemExtra,
) => {
	return await apiClient.post(`/document/${activeDiscourseId}/item`, {
		type: discourseDocumentItemType.discourseTravel,
		extra,
	});
};

export const updateDiscourseTravel = async (
	activeDiscourseId: string,
	itemId: string,
	extra: DiscourseTravelItemExtra,
) => {
	return await apiClient.patch(
		`/document/${activeDiscourseId}/item/${itemId}`,
		{ extra },
	);
};

export const prepareDefaultValues = (document: Document) => {
	const datetimeItem = document?.items?.find(item => item?.type === 'datetime');
	const datetimeText = datetimeItem?.extra?.text;
	const resolvedMoment = datetimeText
		? moment(datetimeText)
		: document?.createdAt
		? moment(document.createdAt)
		: moment();
	return {
		name: document?.items?.find(item => item?.type === 'name')?.extra?.text,
		date: resolvedMoment.toISOString(),
		time: resolvedMoment.clone(),
		isDatetimeManual:
			Boolean(datetimeItem?.extra?.manual) &&
			datetimeItem?.extra?.manualDocumentId === document?.id,
		text: document?.items?.find(item => item?.type === 'content')?.extra?.text,
		diagnosis: document?.items?.find(
			item =>
				item?.type === 'diagnosis' && item?.extra?.subType === 'main_diagnosis',
		)?.extra?.text,
		diagnoses: document?.items
			?.filter(
				item =>
					item?.type === 'diagnosis' &&
					item?.extra?.subType === 'secondary_diagnosis',
			)
			.map(item => {
				return { secondaryDiagnosis: item?.extra?.text };
			}),
		discourseId: document?.id,
	};
};

export const prepareDiscourseItems = (
	values: DiscourseValues,
	raw: boolean = false,
) => {
	const data: { children: object[] } = {
		children: [
			{
				type: 'content',
				extra: {
					text: values.text,
				},
			},
		],
	};

	if (values.name) {
		data.children.push({
			type: 'name',
			extra: {
				text: values.name,
			},
		});
	}

	const dateMoment = values.date ? moment(values.date) : null;
	const timeMoment = values.time ? moment(values.time) : null;

	if (dateMoment?.isValid()) {
		const datetime = dateMoment.clone();
		if (timeMoment?.isValid()) {
			datetime
				.hour(timeMoment.hour())
				.minute(timeMoment.minute())
				.second(0)
				.millisecond(0);
		}

		data.children.push({
			type: 'datetime',
			extra: {
				text: datetime.toISOString(),
				manual: Boolean(values.isDatetimeManual),
				...(values.isDatetimeManual
					? { manualDocumentId: values.discourseId }
					: {}),
			},
		});
	}

	if (values.diagnosis) {
		if (hasDiscourseMainDiagnosis(values.diagnosis)) {
			data.children.push({
				type: 'diagnosis',
				extra: {
					text: values.diagnosis,
					subType: DocumentContentSubType.MAIN_DIAGNOSIS,
				},
			});
		}
	}

	if (values.diagnoses) {
		values.diagnoses.forEach(item => {
			if (item?.secondaryDiagnosis?.label && item?.secondaryDiagnosis?.value) {
				data.children.push({
					type: 'diagnosis',
					extra: {
						text: item?.secondaryDiagnosis,
						subType: DocumentContentSubType.SECONDARY_DIAGNOSIS,
					},
				});
			}
		});
	}

	if (raw) {
		return data?.children;
	}

	return data;
};
